/* eslint-disable no-unused-expressions */
/**
 * Hero Banner
 */
import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter } from 'js/helpers.js';
import calculateReadingTime from 'js/readtime.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { openModal, handleModalClose } from '../../../../modal-container/clientlibs/site/js/modal-container.js';

let componentName = 'hero-banner';
const tabletViewBreakpoint = 1024;

class HeroBanner extends Component {

    /** Component elements */
    elements = {
        main: this.element.closest('main'),
        container: this.element.querySelector(`.${componentName}__container`),
        // Product Experience
        teaserWrapper: this.element.querySelector(`.${componentName}__teaser-wrapper`),
        teaserImage: this.element.querySelector(`.${componentName}__teaser-image`),
        teaserContainer: this.element.querySelector(`.${componentName}__teaser-container.layout-horizontal`),
        // Product Experience Search
        searchProductExperience: this.element.querySelector(`.${componentName}__product-experience-search`),
        searchInput: this.element.querySelector(`.${componentName}__search-input`),
        searchLabel: this.element.querySelector(`.${componentName}__search-label`),
        searchClearButton: this.element.querySelector(`.${componentName}__search-clear`),
        searchClearButtonSpan: this.element.querySelector(`.${componentName}__search-clear span`),
        // Time to read
        readTimeWrapper: this.element.querySelector(`.${componentName}__read-time-wrapper`),
        readTimeText: this.element.querySelector(`.${componentName}__read-time-text`),
        // Animation
        textContainer: this.element.querySelector(`.${componentName}__text-container`),
        teaserTextContent: this.element.querySelector(`.${componentName}__media-container .teaser__content`),
        contentTile: this.element.querySelector(`.${componentName}__media-container .content-tile`),
        teaserShard: this.element.querySelector(`.${componentName}__teaser-shard`),
        teaserFlag: this.element.querySelector(`.${componentName}__teaser-flag`),

        shards1: this.element.querySelectorAll(`.${componentName}__shard-1`),
        shards2: this.element.querySelectorAll(`.${componentName}__shard-2`),
        shards3: this.element.querySelectorAll(`.${componentName}__shard-3`),
        shards4: this.element.querySelectorAll(`.${componentName}__shard-4`),
        shards5: this.element.querySelectorAll(`.${componentName}__shard-5`),
        image: this.element.querySelector(`.${componentName}__image`),
        video: this.element.querySelector(`.${componentName}__video`),
        // Video modal
        videoButton: this.element.querySelector(`.${componentName}__video-button`),
        videoDialog: this.element.querySelector(`.${componentName}__video-dialog`),
        videoDialogBackdrop: this.element.querySelector(`.${componentName}__video-dialog-backdrop`),
    };

    initReadTime = () => {
        if (this.elements.readTimeWrapper && this.elements.readTimeText) {
            const htmlLang = document.documentElement.lang || document.documentElement.getAttribute('lang');
            const metaSite = document.querySelector('meta[property="og:site_name"]')?.content?.toLowerCase();
            const textClass = metaSite && metaSite === 'plex' ? 'px-text' : 'cmp-text';
            const readTime = calculateReadingTime(htmlLang, 'main-wrapper', textClass);
            if (readTime) {
                this.elements.readTimeText.innerHTML = readTime;
                this.elements.readTimeWrapper.hidden = false;
            }
        }
    };

    initWebinarActions = () => {
        // Display webinar label (Series, Upcoming, or On-Demand)
        const isWebinarSeries = document.getElementsByTagName('body')[0].classList.contains('webinarseries');
        const heroText = document.querySelector(`.${componentName}__text-container`);
        if (isWebinarSeries && heroText) {
            heroText.setAttribute('data-webinar-status', 'series');
        }

        // Display webinar Thank You experience when Gated Form submitted
        const heroVariant = document.querySelector(`.${componentName}__container`).dataset.designVariant;
        if (heroVariant === 'webinar') {
            let hiddenResources = document.querySelector('.generic-container:has(#webinar-related-resources)');
            if (isEditModeFooter() && hiddenResources !== null) {
                hiddenResources.style.display = 'block';
            }
            if (hiddenResources === null) {
                // Support legacy setup (last Related Content instead of Generic Container ID)
                const genericContainers = document.querySelectorAll('.generic-container');
                const relatedContentContainers = document.querySelectorAll('.generic-container:has(.related-content)');
                hiddenResources = relatedContentContainers[relatedContentContainers.length - 1];
                if(!isEditModeFooter() && genericContainers.length > 1) {
                    hiddenResources.style.display = 'none';
                }
            }

            const interval = setInterval(() => {
                const formType = document.querySelector('input[name="$FormFunction"]');
                if (formType) {
                    clearInterval(interval);

                    if (formType.getAttribute('value') === 'general' || formType.getAttribute('value') === 'webinar') {
                        const webinarForm = document.querySelector('form.gcdc-form-render');
                        const observer = new MutationObserver((mutations) => {
                            mutations.forEach((mutation) => {
                                if (mutation.type === 'attributes') {
                                    if (webinarForm.getAttribute('style') !== 'display: block;') {
                                        // Hide unwanted content
                                        webinarForm.closest('.generic-container__inner').style.display = 'none';
                                        const webinarLabels = document.querySelectorAll(`.${componentName}__webinar-label`);
                                        for (let i = 0; i < webinarLabels.length; i += 1) {
                                            webinarLabels[i].style.display = ('none');
                                        }
                                        document.querySelector(`.${componentName}__title`).style.display = 'none';
                                        document.querySelector(`.${componentName}__copy-text`).style.display = 'none';
                                        document.querySelector(`.${componentName}__buttons-container`).style.display = 'none';
                                        // Display confirmation (Thank You) content
                                        const webinarSingleInfo = document.querySelector('.webinar-info__item.startDate');
                                        const webinarSeriesInfo = document.querySelector('.webinar-series__container');
                                        let thankYouEmail = '';
                                        if (webinarSingleInfo !== null) {
                                            thankYouEmail = webinarSingleInfo.dataset.thankyouEmail;
                                        } else if (webinarSeriesInfo !== null) {
                                            thankYouEmail = webinarSeriesInfo.dataset.thankyouEmail;
                                        }
                                        if (thankYouEmail !== '' && thankYouEmail !== 'NAWebinars@ra.rockwell.com') {
                                            const emailLink = document.querySelector('.webinar-thankyou-email');
                                            emailLink.setAttribute('href', `mailto:${thankYouEmail}`);
                                            emailLink.innerHTML = thankYouEmail;
                                        }
                                        document.querySelector(`.${componentName}__webinar-thank-you`).style.display = 'block';
                                        if (hiddenResources !== null) {
                                            hiddenResources.style.display = 'block';
                                        }
                                        window.scroll(0, 0);
                                    }
                                }
                            });
                        });

                        observer.observe(webinarForm, {
                            attributes: true
                        });
                    }
                }
            }, 500);
        }
    };

    // This sets the teaser image size for PRODUCT EXPERIENCE variant of the hero banner
    setTeaserImageSize = () => {
        this.elements.teaserWrapper = this.element.querySelector('.teaser__wrapper');
        this.elements.teaserImage = this.element.querySelector('.teaser__image');
        this.elements.teaserContainer = this.element.querySelector('.teaser__container.layout-horizontal');
        if (this.elements.teaserWrapper && this.elements.teaserImage && this.elements.teaserContainer) {
            if (window.innerWidth > tabletViewBreakpoint) {
                this.elements.teaserImage.style.height = `${this.elements.teaserWrapper.offsetHeight}px`;
                this.elements.teaserImage.style.width = '30%';
            } else {
                this.elements.teaserImage.style.width = `${this.elements.teaserWrapper.offsetWidth}px`;
                this.elements.teaserImage.style.height = '81px';
            }
        }
    };

    moveTeaserShard = () => {
        if(this.elements.teaserTextContent && !this.element.querySelector(`.${componentName}__media-container .content-tile .teaser__content .${componentName}__teaser-shard`)) {
            const teaserShardClone = this.elements.teaserShard?.cloneNode(true);
            this.elements.teaserTextContent?.prepend(teaserShardClone);
            const teaserFlag = this.element.querySelector('.product-experience__teaser-flag');
            this.elements.teaserTextContent?.prepend(teaserFlag);
        }
    };

    getPosition = (string, subString, index) => string.split(subString, index).join(subString).length;

    submitSearch = (searchTerm) => {
        const searchLink = this.elements.searchProductExperience.getAttribute('data-search-link');
        const escapedSearchTerm = encodeURIComponent(searchTerm).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16)}`);
        window.location.href = `${searchLink}.html?keyword=${escapedSearchTerm || this.elements.searchInput.value}`;
    };

    initProductExperience = () => {
        if(this.elements.contentTile) {
            const observer = new MutationObserver((mutationsList) => {
                mutationsList.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        this.elements.teaserTextContent = this.element.querySelector(`.${componentName}__media-container .teaser__content`);
                        if(!this.element.querySelector(`.${componentName}__media-container .content-tile .${componentName}__teaser-flag`)) {
                            this.moveTeaserShard();
                        }
                        this.elements.teaserShard.remove();
                        this.setTeaserImageSize();
                    }
                });
            });

            const config = { childList: true };
            observer.observe(this.elements.contentTile, config);
        } else {
            this.elements.teaserShard.remove();
        }

        if(this.elements.searchClearButton) {
            this.elements.searchClearButton.hidden = true;
        }
    };

    // eslint-disable-next-line consistent-return
    waitForElement = (selector) => new Promise((resolve) => {
        if (document.querySelector(selector)) {
            // eslint-disable-next-line no-promise-executor-return
            return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(() => {
            if (document.querySelector(selector)) {
                observer.disconnect();
                resolve(document.querySelector(selector));
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });

    initAnimation = () => {
        if (this.elements.shards1.length < 1) {
            return;
        }

        gsap.registerPlugin(ScrollTrigger);
        const scrollTrigger = {
            trigger: this.element,
            start: `top ${this.elements.main?.offsetTop ?? 0}px`,
            end: 'bottom top',
            scrub: true
        };

        const gsapToIfExists = (target, vars) => {
            if (target) {
                gsap.to(target, vars);
            }
        };

        gsapToIfExists(this.elements.shards1, { y: -100, scrollTrigger });
        gsapToIfExists(this.elements.shards2, { y: -150, scrollTrigger });
        gsapToIfExists(this.elements.shards3, { y: 60, scrollTrigger });
        gsapToIfExists(this.elements.shards4, { y: 400, scrollTrigger });
        gsapToIfExists(this.elements.shards5, { y: 600, scrollTrigger });
        gsapToIfExists(this.elements.image, { scale: 1.4, scrollTrigger });
        gsapToIfExists(this.elements.video, { scale: 1.4, scrollTrigger });
    };

    /** Open modal video */
    handleVideoButton = () => {
        openModal(this.elements.videoDialog);
        this.elements.videoDialog.querySelector('video')?.play();
    };

    /** Search Input **/
    handleSearchInput = () => {
        this.elements.searchInput.setAttribute('placeholder', '');
        this.elements.searchLabel.classList.add('focused-position');
        if (this.elements.searchInput.value.length > 0) {
            this.elements.searchClearButton.hidden = false;
            this.elements.searchInput.classList.add('dark-border');
        } else {
            this.elements.searchClearButton.hidden = true;
            this.elements.searchInput.classList.remove('dark-border');
        }
    };

    handleSearchInputOut = () => {
        if (this.elements.searchInput.value.length === 0) {
            this.elements.searchLabel.classList.remove('focused-position');
        }
    };

    handleSearchClearButton = () => {
        this.elements.searchInput.classList.remove('dark-border');
        this.elements.searchInput.value = '';
        this.elements.searchClearButton.hidden = true;
        this.elements.searchLabel.classList.remove('focused-position');
    };

    addEventListeners = () => {
        this.elements.videoButton?.addEventListener('click', this.handleVideoButton);
        this.elements.videoDialog?.addEventListener('close', handleModalClose);
        this.elements.searchInput?.addEventListener('focus', this.handleSearchInput);
        this.elements.searchInput?.addEventListener('focusout', this.handleSearchInputOut);
        this.elements.searchInput?.addEventListener('input', this.handleSearchInput);
        this.elements.searchClearButton?.addEventListener('click', this.handleSearchClearButton);
        this.elements.searchInput?.addEventListener('keyup', (ev) => {
            if (ev.key === 'Enter') {
                this.submitSearch(this.elements.searchInput.value);
            }
        });
        this.elements.videoDialogBackdrop?.addEventListener('click', () => this.elements.videoDialog.close());
        if (this.elements.container.getAttribute('data-design-variant') === 'product-experience') {
            window.onresize = () => {
                this.initAnimation();
                this.setTeaserImageSize();
            };
        }
    };

    /** Initialize components */
    init() {
        componentName = this.element.querySelector(`.${componentName}-sns__container`) ? `${componentName}-sns` : componentName;
        this.initReadTime();
        this.initWebinarActions();
        this.initAnimation();
        if (this.elements.container.getAttribute('data-design-variant') === 'product-experience') {
            if (this.elements.searchInput) {
                this.elements.searchInput.value = new URLSearchParams(window.location.search).get('keyword') || '';
            }
            this.initProductExperience();
        }
        this.addEventListeners();
    }
}

registerComponent('.hero-banner', HeroBanner);
